import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingWelcomeStep1, billingWelcomeStep2 } from '@ibk/shared-assets';
import { useAuth, useIntegrationValidation, useSelectedAccount } from '@ibk/shared-auth';
import { BillingProductsSlugType, selectContaDigitalProduct, useAppSelector, useGetProductRelationShips, } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ApiProductStatus, FeatureFlagUseCase } from '@ibk/shared-types';
import { BillingTermVersionStatusType } from '../../enums';
import { useAcceptTermMutation, useLazyGetProductBySlugQuery, useLazyGetValidateContractQuery, } from '../../services/contractApi';
const isAlreadyAcceptedError = (err) => {
    if (typeof err !== 'object' || err === null)
        return false;
    const candidate = err;
    return candidate.status === 409 || candidate.status === '409' || candidate.originalStatus === 409;
};
export function useWelcomeWizard() {
    const steps = React.useMemo(() => [
        {
            id: 'billing-welcome-modal-step-1',
            img: billingWelcomeStep1,
            step: 1,
        },
        {
            id: 'billing-welcome-modal-step-2',
            img: billingWelcomeStep2,
            step: 2,
        },
    ], []);
    const navigate = useNavigate();
    const { data: auth } = useAuth();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(auth?.personId, { autoLoad: true });
    const isFreeNfseEmission = !!isFeatureFlagWhiteListed(FeatureFlagUseCase.BILLING_ENABLE_FREE_NFSE_EMISSION);
    const [getProductBySlugQuery] = useLazyGetProductBySlugQuery();
    const [getValidateQuery] = useLazyGetValidateContractQuery();
    const [acceptTermMutation] = useAcceptTermMutation();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [stepCurrent, setStepCurrent] = React.useState(steps[0]);
    const [isInvalidProduct, setIsInvalidProduct] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const { getBillingProduct, saveBillingProduct } = useGetProductRelationShips();
    const { selectedAccount } = useSelectedAccount();
    const contaDigitalProduct = useAppSelector(selectContaDigitalProduct);
    const isContaDigitalNotContracted = contaDigitalProduct?.data?.status === ApiProductStatus.AVAILABLE ||
        contaDigitalProduct?.data?.status === ApiProductStatus.ACTIVATING;
    const { hasInvalidIntegrationData, isIntegrationAlertVisible, handleCloseIntegrationAlert } = useIntegrationValidation();
    const validateTermAccepted = async () => {
        setIsLoading(true);
        try {
            if (!auth?.personId) {
                setIsLoading(false);
                return;
            }
            const hasModuleBilling = isFeatureFlagWhiteListed(FeatureFlagUseCase.NEW_IBKS_ESCRITORIO_MODULE_BILLING);
            if (!hasModuleBilling) {
                setIsLoading(false);
                return;
            }
            const billingProduct = getBillingProduct();
            if (billingProduct) {
                setIsLoading(false);
                return;
            }
            const { data: productData } = await getProductBySlugQuery({ slug: BillingProductsSlugType.BILLING_MANAGEMENT });
            if (!productData) {
                setIsLoading(false);
                return;
            }
            setProduct(productData);
            setIsOpen(true);
        }
        catch (error) {
            setIsOpen(false);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        validateTermAccepted();
    }, []);
    useEffect(() => {
        if (auth?.personId) {
            validateTermAccepted();
        }
    }, [auth?.personId, isFeatureFlagWhiteListed]);
    const updateStep = (direction) => {
        setStepCurrent(prev => {
            const delta = direction === 'next' ? 1 : -1;
            const nextStep = prev.step + delta;
            const nextWizard = steps.find(w => w.step === nextStep);
            return nextWizard ?? prev;
        });
    };
    const handlerConfirmTerm = async () => {
        setIsLoading(true);
        try {
            if (!selectedAccount ||
                !product ||
                !selectedAccount?.targetId ||
                !selectedAccount?.documentNumber ||
                !product.terms?.termVersion?.id ||
                hasInvalidIntegrationData ||
                isContaDigitalNotContracted) {
                setIsInvalidProduct(true);
                setIsLoading(false);
                return;
            }
            const { data } = await getValidateQuery();
            if (!data?.success) {
                setIsInvalidProduct(true);
                setIsLoading(false);
                return;
            }
            try {
                await acceptTermMutation({
                    productId: product.id,
                    partyId: selectedAccount.targetId,
                    termVersionId: product.terms.termVersion.id,
                    referenceType: 'PARTY',
                    referenceValue: selectedAccount.targetId,
                    status: BillingTermVersionStatusType.ACCEPTED,
                    metadata: { documentNumber: selectedAccount.documentNumber },
                }).unwrap();
            }
            catch (acceptError) {
                if (!isAlreadyAcceptedError(acceptError)) {
                    throw acceptError;
                }
            }
            await saveBillingProduct();
            setIsOpen(false);
            navigate('/cobrancas', { state: { activeTab: 'home' } });
        }
        catch (error) {
            setIsInvalidProduct(true);
        }
        finally {
            setIsLoading(false);
        }
    };
    return {
        isLoading,
        product,
        stepCurrent,
        updateStep,
        steps,
        isOpen,
        setIsOpen,
        handlerConfirmTerm,
        isInvalidProduct,
        showTermsModal,
        setShowTermsModal,
        hasInvalidIntegrationData,
        isIntegrationAlertVisible,
        handleCloseIntegrationAlert,
        isContaDigitalNotContracted,
        isFreeNfseEmission,
    };
}
